const TW_MULTIPLIER = 4;

const round = num =>
  num
    .toFixed(7)
    .replace(/(\.[0-9]+?)0+$/, '$1')
    .replace(/\.0$/, '');

const remify = ({ pixels = null, increments = null }) => {
  if (pixels) {
    return `${round(pixels / 16)}rem`;
  } else if (increments) {
    return `${round((increments * TW_MULTIPLIER) / 16)}rem`;
  }
};

const beforeAfter = ['before', 'after'];

const communityColours = {
  bellarine: '#5F9BC6',
  'berwick-waters': '#e2cc00',
  bittern: '#ff5859',
  brookfield: '#007A4D',
  'casey-fields': '#FDB924',
  'chelsea-heights': '#63B1E5',
  deanside: '#890C58',
  geelong: '#73CDD7',
  hastings: '#7C6A55',
  'kaduna-park': '#BC955C',
  lyndarum: '#8F4E7D',
  meridian: '#005844',
  merrifield: '#DA1C54',
  'mount-duneed': '#0f0e9f',
  'ocean-grove': '#00c4b4',
  'ocean-grove-the-cove': '#6884A1',
  officer: '#487637',
  'phillip-island': '#407EC9',
  ridgelea: '#FDBA61',
  riverfield: '#492A7E',
  seasons: '#C24D52',
  shepparton: '#80BA27',
  'st-leonards': '#005A70',
  'st-leonards-the-shores': '#E17262',
  warragul: '#A2AD00',
  wollert: '#A4123F',
  woodlea: '#F6B700',
  yarrawonga: '#274085',
};

const safelist = [
  ...Object.keys(communityColours).map(
    communityName => `text-community-${communityName}`
  ),
  ...Object.keys(communityColours).map(
    communityName => `fill-community-${communityName}`
  ),
  ...Object.keys(communityColours).map(
    communityName => `bg-community-${communityName}`
  ),
  ...Object.keys(communityColours).map(
    communityName => `border-community-${communityName}`
  ),
  'fill-secondary',
  'fill-white',
  'bg-filter-dropdown-white',
  'bg-filter-dropdown-grey-100',
];

module.exports = {
  mode: 'jit', // https://tailwindcss.com/docs/just-in-time-mode
  purge: {
    content: ['./craft/templates/**/*.twig', './src/**/*.{js,jsx,ts,tsx,vue}'],
    safelist,
  },
  darkMode: false, // or 'media' or 'class'
  theme: {
    screens: {
      xs: '375px',
      sm: '450px',
      md: '768px',
      lg: '1024px',
      xl: '1320px',
      '2xl': '1440px',
      '2-3xl': '1528px',
      '2-5xl': '1768px',
      '3xl': '1920px',
      mobile: { max: '767px' },
      tablet: { min: '768px', max: '1023px' },
      'mobile+tablet': { max: '1023px' },
    },
    container: {
      center: true,
      padding: {
        DEFAULT: '12px',
        xs: '20px',
        lg: '24px',
      },
    },
    colors: {
      transparent: 'transparent',
      current: 'currentColor',
      initial: 'initial',
      black: '#000',
      white: '#fff',
      primary: {
        DEFAULT: '#ed8b00',
        100: '#fadfb2',
        200: '#f7ca7f',
        300: '#f4b54d',
        400: '#f2a527',
        500: '#f19602',
        600: '#ed8b00', // default
        700: '#e77b00',
        800: '#e16c00',
        900: '#d85300',
        navy: '#0b2537',
      },
      secondary: {
        DEFAULT: '#fff4ea',
        darker: '#ede3da',
      },
      body: '#404e57',
      heading: '#202020',
      grey: {
        DEFAULT: '#9e9e9e',
        100: '#f8f7f7',
        200: '#f0f0f0',
        300: '#bbbbbb',
        400: '#b1b1b1',
        500: '#9e9e9e', // default
        600: '#7e7e7e',
        700: '#5f5f5f',
        800: '#3f3f3f',
        900: '#202020',
      },
      success: {
        DEFAULT: '#eec372',
        400: '#47c980',
        500: '#33c372', // default
        600: '#2eb067',
      },
      warning: {
        DEFAULT: '#ffba38',
        400: '#ffc860',
        500: '#ffba38',
        600: '#e6a732',
      },
      error: {
        DEFAULT: '#df1642',
        400: '#e54568',
        500: '#df1642',
        600: '#b21235',
      },
      community: communityColours,
      socials: {
        facebook: '#3B5998',
        youtube: '#F52929',
        linkedin: '#007AB7',
        zoom: '#2196F3',
      },
      'home-tag': {
        existing: '#E2FCC8',
        new: '#C8CEFC',
      },
    },
    fontFamily: {
      circular: 'Circular Std',
      betterTimes: 'Better Times',
      sansSerif: 'sans-serif',
    },
    fontSize: {
      none: 0,
      '2xs': remify({ pixels: 12 }),
      xs: remify({ pixels: 14 }),
      sm: remify({ pixels: 16 }),
      base: remify({ pixels: 18 }),
      md: remify({ pixels: 22 }),
      lg: remify({ pixels: 24 }),
      xl: remify({ pixels: 26 }),
      '2xl': remify({ pixels: 28 }),
      '3xl': remify({ pixels: 32 }),
      '4xl': remify({ pixels: 36 }),
      '5xl': remify({ pixels: 44 }),
      '6xl': remify({ pixels: 62 }),
      '7xl': remify({ pixels: 78 }),
      '7xl': remify({ pixels: 78 }),
      '8xl': remify({ pixels: 100 }),
      'bt-md': remify({ pixels: 96 }),
      'bt-lg': remify({ pixels: 126 }),
      'bt-xl': remify({ pixels: 156 }),
    },
    lineHeight: {
      none: '1',
      xs: '1.15',
      base: '1.25',
      md: '1.4',
      lg: '1.5',
    },
    extend: {
      width: {
        'grid-1/3': 'calc((100% - (26px * 2)) / 3)',
        'grid-1/2': 'calc((100% - 26px) / 2)',
        'events-mobile-swiper': 'calc(100% - 20px)',
      },
      height: {
        18: '4.5rem',
        contentCardSmall: '238px',
        contentCardLarge: '500px',
        testimonial: '514px',
        'filter-dropdown-gradient': 'calc(100% - 2px)',
        'full-iframe-desktop': 'calc(100vh - 76px)',
        'full-iframe-mobile': 'calc(100vh - 72px)',
        heroWide: '558px',
        heroCommunityWide: '500px',
        heroWideSlim: '344px',
      },
      minHeight: {
        hero: '500px',
        heroSlim: '364px',
        heroCommunity: '348px',
      },
      maxHeight: {
        hero: '500px',
        heroSlim: '364px',
      },
      minWidth: {
        heroCommunity: '619px',
      },
      spacing: {
        6.5: remify({ increments: 6.5 }),
        13: remify({ increments: 13 }),
        15: remify({ increments: 15 }),
        17: remify({ increments: 17 }),
        18: remify({ increments: 18 }),
        19: remify({ increments: 19 }),
        22.5: remify({ increments: 22.5 }),
        25: remify({ increments: 25 }),
        27: remify({ increments: 27 }),
        30: remify({ increments: 30 }),
        40: remify({ increments: 40 }),
        grid: '26px',
        'header-mobile': '72px',
        'header-desktop': '116px',
        'header-desktop-slim': '76px',
        'header-top-mobile': 0,
        'header-top-desktop': '40px',
        'header-main-mobile': '72px',
        'header-main-desktop': '76px',
        'header-secondary-mobile': '76px',
        'header-secondary-desktop': '76px',
      },
      outline: {
        default: ['2px solid #202020', '4px'],
      },
      boxShadow: {
        header: '0px 1px 20px rgba(112, 144, 176, 0.2)',
        'button-secondary': '0 0 0 1px #ed8b00',
        'button-secondary-zoom': '0 0 0 1px #ed8b00',
        'button-third': '0px 4px 10px 6px rgba(114, 118, 122, 0.16)',
        'why-benefits-card': '0px 4px 10px 6px rgba(114, 118, 122, 0.16)',
        'swiper-buttons': '0px 4px 10px 6px rgba(114, 118, 122, 0.16)',
        'enquiry-form': '0px 4px 10px 6px rgba(114, 118, 122, 0.16)',
        'subscription-form': '0px 1px 20px rgba(112, 144, 176, 0.2)',
        'light-20': '0px 1px 4px rgba(112, 144, 176, 0.1)',
        'light-40': '0px 2px 4px 1px rgba(112, 144, 176, 0.11)',
        'light-60': '0px 1px 20px rgba(112, 144, 176, 0.2)',
        'light-80': '0px 4px 10px 6px rgba(114, 118, 122, 0.16)',
        'light-100': '0px 6px 24px rgba(114, 118, 122, 0.5)',
      },
      transitionDelay: {
        none: '0ms',
      },
      backgroundImage: {
        'content-card-gradient':
          'linear-gradient(0deg, rgba(22, 20, 23, 0.86) 8.67%, rgba(64, 56, 56, 0.15) 61.68%)',
        'form-error': 'url(../icons/icon-phone.svg)',
        'trusted-partner-card':
          'linear-gradient(0deg, rgba(23, 20, 20, 0.86) 8.67%, rgba(64, 56, 56, 0.15) 61.68%)',
        'filter-dropdown-white':
          'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 75%)',
        'filter-dropdown-grey-100':
          'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(248, 247, 247, 1) 75%)',
        'hero-gradient':
          'radial-gradient(50% 132.41% at 0% 100%, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0) 100%)',
        'hero-community-gradient':
          'linear-gradient(0deg, rgba(14, 37, 55, 0.80) 0%, rgba(14, 37, 55, 0.80) 100%)',
      },
      borderWidth: {
        1: '1px',
      },
      inset: {
        4.5: '1.25rem',
        '2/12': '16.67%',
      },
      zIndex: {
        '-1': '-1',
        1: 1,
        2: 2,
        3: 3,
        100: '100',
        max: '9999999999',
        chatbot: 2147483639,
        'chatbot-front': 2147483640,
        'chatbot-back': 2147483638,
      },
      content: {
        blank: '""',
        bullet: '"\\2022"',
      },
      borderRadius: {
        '4xl': '2rem',
      },
      fill: Object.assign(
        {},
        {
          white: '#ffffff',
          primary: '#ed8b00',
          secondary: '#fff4ea',
        },
        {
          community: communityColours,
        }
      ),
    },
  },
  variants: {
    extend: {
      opacity: ['group-focus-within'],
      inset: ['group-hover'],
      fontSize: ['group-hover'],
    },
    backgroundImage: [...beforeAfter],
    backgroundColor: [...beforeAfter],
    position: [...beforeAfter],
    inset: [...beforeAfter],
    width: [...beforeAfter],
    height: [...beforeAfter],
  },
  plugins: [
    require('@tailwindcss/aspect-ratio'),
    require('tailwindcss-interaction-variants'),
  ],
};
